'use client';

import { useState } from 'react';

import { SanityImage } from '@/components/shared/SanityImage';
import type { BenefitsGuaranteesSectionData } from '@/sanity/types';

interface BenefitsGuaranteesProps {
  data: BenefitsGuaranteesSectionData;
}

/**
 * "Benefits & guarantees" tabbed section.
 *
 * Dark section with:
 *  - Full-width H2 ("Benefits &" white + "guarantees" green accent)
 *  - Tab switcher (top-right on desktop, below heading on mobile)
 *  - Left: decorative cube image with green `mix-blend-hue` overlay
 *  - Right: paragraph content controlled by the selected tab
 *
 * Accessibility (WAI-ARIA tabs pattern):
 *  - <section aria-labelledby="benefits-heading">
 *  - role="tablist" + role="tab" + role="tabpanel" with aria-controls/aria-selected/aria-labelledby
 *  - Arrow-key navigation between tabs; Home/End jump to first/last
 *  - All panels stay in the DOM; inactive ones use the `hidden`
 *    attribute so the copy is SSR'd and findable (conversion-h5ur7m).
 */
export function BenefitsGuarantees({ data }: BenefitsGuaranteesProps) {
  const tabs = data.tabs ?? [];
  const [activeIndex, setActiveIndex] = useState(0);

  if (tabs.length === 0) return null;


  function handleKey(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = tabs.length - 1;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveIndex(index === last ? 0 : index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveIndex(index === 0 ? last : index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(last);
    }
  }

  return (
    <section
      id="benefits"
      aria-labelledby="benefits-heading"
      data-section-id="benefits_guarantees"
      className="relative overflow-hidden bg-background py-16 md:py-20 lg:py-24 xl:py-28"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        {/* ----- Heading + tabs row ----- */}
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
          <h2
            id="benefits-heading"
            className="max-w-[640px] font-light leading-[0.961] text-[40px] md:text-[60px] lg:text-[76px] xl:text-[88px] 2xl:text-[98px] tracking-[-0.022em] md:tracking-[-0.027em] lg:tracking-[-0.029em] xl:tracking-[-0.031em] 2xl:tracking-[-0.032em]"
          >
            <span className="text-foreground">
              {data.benefitsHeadingText ?? 'Benefits &'}{' '}
            </span>{' '}
            <span className="block text-accent lg:inline">
              {data.benefitsHeadingAccent ?? 'guarantees'}
            </span>
          </h2>

          {/* Tab switcher */}
          <div
            role="tablist"
            aria-label="Benefits and guarantees tabs"
            className="flex shrink-0 self-start"
          >
            {tabs.map((tab, index) => {
              const selected = index === activeIndex;
              const tabId = `benefits-tab-${index}`;
              const panelId = `benefits-tabpanel-${index}`;
              return (
                <button
                  key={`${tab.label}-${index}`}
                  id={tabId}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  aria-controls={panelId}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActiveIndex(index)}
                  onKeyDown={(event) => handleKey(event, index)}
                  data-analytics-cta={`benefits_tab_${tab.label.toLowerCase()}`}
                  data-analytics-location="benefits_guarantees"
                  className={
                    'flex cursor-pointer h-[56px] items-center justify-center border-[0.5px] px-6 py-4 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background md:h-[72px] md:w-[190px] md:px-8 ' +
                    (selected
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-[#a3a3a3] bg-transparent text-foreground hover:bg-white/5')
                  }
                >
                  <span className="flex items-center gap-1.5">
                    <span className="text-[14px] font-normal uppercase tracking-[-0.02em] underline decoration-solid md:text-[20px] md:tracking-[-0.4px]">
                      {tab.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ----- Content: cube image + paragraphs ----- */}
        <div className="mt-12 flex flex-col gap-12 lg:mt-16 lg:flex-row lg:items-start lg:gap-16 xl:gap-20">
          {/* Decorative cube image */}
          <div
            className="pointer-events-none relative mx-auto hidden aspect-square w-full max-w-[492px] select-none lg:block lg:w-[38%] xl:w-[35%]"
            aria-hidden="true"
          >
            <SanityImage
              image={data.decorativeImage}
              fallbackSrc="/images/decorative/about-cubes.png"
              alt=""
              fill
              sizes="(max-width: 1024px) 0px, 35vw"
              className="object-contain"
            />
            <div className="absolute inset-0 bg-[#4dd017] mix-blend-hue" />
          </div>

          {/* Paragraphs panels. ALL tabs render in the DOM and inactive ones
              are toggled with the `hidden` attribute instead of a conditional
              render (finding conversion-h5ur7m): the Guarantees copy was
              invisible to SSR HTML, scanners, and find-in-page until the
              second tab was clicked. */}
          <div className="flex-1">
            {tabs.map((tab, index) => (
              <div
                key={`${tab.label}-panel-${index}`}
                id={`benefits-tabpanel-${index}`}
                role="tabpanel"
                aria-labelledby={`benefits-tab-${index}`}
                tabIndex={0}
                hidden={index !== activeIndex}
                className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-background"
              >
                <div className="flex max-w-[631px] flex-col gap-5 lg:gap-6">
                  {tab.paragraphs.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-[16px] font-light leading-[1.36] text-foreground md:text-[18px] xl:text-[20px]"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile cube image rendered below content */}
        <div
          className="mt-12 flex justify-center lg:hidden"
          aria-hidden="true"
        >
          <div className="relative aspect-square w-full max-w-[320px]">
            <SanityImage
              image={data.decorativeImage}
              fallbackSrc="/images/decorative/about-cubes.png"
              alt=""
              fill
              sizes="320px"
              className="object-contain"
            />
            <div className="absolute inset-0 bg-[#4dd017] mix-blend-hue" />
          </div>
        </div>
      </div>
    </section>
  );
}
